import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import HeaderBar from "../../students/components/headerBar";
import ProfileInformation from "./ProfileInfo";
import ProfileOverview from "./ProfileOverview";
import ChangePassword from "./changePassword";
import {
  User,
  Lock,
  LogOut,
  Camera,
  Settings2,
  UserCircle,
  Shield,
  Smartphone,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import {
  useAuthState,
  useLogout,
  useAuthicatedUser,
  extractErrorMessages,
} from "react-redux-django-auth";
import { authClientWeb } from "react-redux-django-auth/web";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Button from "../../../components/common/Button";
import { useToast } from "../../../hooks/otherHooks/useToast";

const Profile = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  const validTabs = ["profile-info", "edit-profile", "security", "password"];
  const initialTab =
    tabParam && validTabs.includes(tabParam)
      ? tabParam
      : location.pathname.endsWith("/settings")
        ? "edit-profile"
        : "profile-info";

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (location.pathname.endsWith("/settings")) {
      setActiveTab("edit-profile");
    }
  }, [tabParam, location.pathname]);

  const { user: rawUser } = useAuthState();
  const { refreshUser } = useAuthicatedUser();
  const { addToast } = useToast();
  const currentUser =
    (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;
  const { handleLogout } = useLogout();
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [savingAvatar, setSavingAvatar] = useState<boolean>(false);
  const [avatarFeedback, setAvatarFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const logoutUser = () => {
    handleLogout();
    navigate("/login", { replace: true });
  };

  const resolvedName =
    currentUser?.first_name || currentUser?.last_name
      ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim()
      : currentUser?.email || "User";

  const formatRole = (r?: string) => {
    if (!r) return "";
    const lower = r.toLowerCase();
    if (lower === "superadmin") return "Super Administrator";
    if (lower === "admin") return "Administrator";
    if (lower === "instructor") return "Instructor";
    if (lower === "student") return "Student";
    return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
  };

  const resolvedRole =
    formatRole(currentUser?.role) ||
    formatRole(currentUser?.user_type) ||
    (currentUser?.is_superuser
      ? "Super Administrator"
      : currentUser?.is_staff
        ? "Administrator"
        : "Student");

  const isValidPhoto = (url?: string | null) =>
    Boolean(
      url &&
      typeof url === "string" &&
      !url.includes("/None") &&
      !url.endsWith("null"),
    );

  const resolvedPic =
    (isValidPhoto(currentUser?.photo_url) ? currentUser.photo_url : null) ||
    (isValidPhoto(currentUser?.get_photo_url)
      ? currentUser.get_photo_url
      : null) ||
    (isValidPhoto(currentUser?.profile_picture)
      ? currentUser.profile_picture
      : null) ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(resolvedName)}&background=3e57da&color=fff&bold=true`;

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
      setSavingAvatar(true);
      setAvatarFeedback(null);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("photo", file);
        formData.append("profile_picture", file);

        const api = authClientWeb();
        if (!api) throw new Error("Authentication client not initialized");

        const response = await api.post(
          "/account/user/profile-picture/",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (response?.data?.photo_url) {
          setAvatarPreview(response.data.photo_url);
        }

        if (refreshUser) {
          await refreshUser();
        }

        const successMsg = "Profile photo updated successfully!";
        if (addToast) {
          addToast({
            message: successMsg,
            bg: "success",
          });
        }
        setAvatarFeedback({
          type: "success",
          message: successMsg,
        });
      } catch (err: any) {
        console.error("Failed to update profile picture:", err);
        setAvatarPreview(null);
        const msgs = extractErrorMessages ? extractErrorMessages(err) : [];
        const errMsg =
          Array.isArray(msgs) && msgs.length > 0
            ? msgs.join(", ")
            : err?.response?.data?.error ||
              err?.response?.data?.detail ||
              "Failed to update profile photo.";

        if (addToast) {
          addToast({
            message: errMsg,
            bg: "danger",
          });
        }
        setAvatarFeedback({
          type: "error",
          message: errMsg,
        });
      } finally {
        setSavingAvatar(false);
      }
    }
  };

  const tabs = [
    { id: "profile-info", label: "Profile Information", icon: UserCircle },
    { id: "edit-profile", label: "Edit Profile", icon: Settings2 },
    { id: "security", label: "Security & 2FA", icon: Shield },
    { id: "password", label: "Change Password", icon: Lock },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Account Settings" />

      <main className="cht-container cht-pb-20">
        <div className="cht-bg-white cht-rounded-[48px] cht-shadow-[0_20px_80px_rgba(0,0,0,0.03)] cht-border cht-border-gray-50 cht-overflow-hidden cht-flex cht-flex-col lg:cht-flex-row cht-min-h-[750px]">
          {/* Left Panel - Profile Summary */}
          <aside className="cht-w-full lg:cht-w-[340px] cht-bg-gray-50/30 cht-border-r cht-border-gray-100 cht-p-10 cht-flex cht-flex-col cht-items-center">
            {/* Avatar Section */}
            <div className="cht-relative cht-w-36 cht-h-36 cht-mb-8">
              <div className="cht-w-full cht-h-full cht-rounded-full cht-overflow-hidden cht-ring-4 cht-ring-white cht-shadow-2xl">
                <img
                  src={avatarPreview || resolvedPic}
                  className="cht-w-full cht-h-full cht-object-cover"
                  alt={resolvedName}
                />
              </div>

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="cht-hidden"
                onChange={handleAvatarChange}
              />

              <motion.button
                type="button"
                disabled={savingAvatar}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                className="cht-absolute cht-bottom-0 cht-right-0 cht-w-10 cht-h-10 cht-rounded-full cht-bg-[#3e57da] cht-text-white hover:cht-bg-[#2d41a8] cht-ring-4 cht-ring-white cht-shadow-lg cht-flex cht-items-center cht-justify-center cht-transition-all cht-cursor-pointer cht-outline-none focus:cht-ring-4 focus:cht-ring-[#3e57da]/30 disabled:cht-opacity-75"
                onClick={() => fileInputRef.current?.click()}
                title="Change profile photo"
                aria-label="Change profile photo"
              >
                {savingAvatar ? (
                  <Loader2
                    size={18}
                    className="cht-text-white cht-animate-spin"
                  />
                ) : (
                  <Camera size={18} className="cht-text-white" />
                )}
              </motion.button>
            </div>

            <div className="cht-text-center cht-space-y-1 cht-mb-8">
              <h3 className="cht-text-xl cht-font-black cht-text-black">
                {resolvedName}
              </h3>
              <p className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                {resolvedRole}
              </p>

              {avatarFeedback && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`cht-mt-3 cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[11px] cht-font-bold cht-flex cht-items-center cht-gap-1.5 ${
                    avatarFeedback.type === "success"
                      ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200"
                      : "cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200"
                  }`}
                >
                  {avatarFeedback.type === "success" ? (
                    <CheckCircle2 size={14} />
                  ) : (
                    <AlertCircle size={14} />
                  )}
                  <span>{avatarFeedback.message}</span>
                </motion.div>
              )}
            </div>

            {/* Vertical Menu */}
            <nav className="cht-w-full cht-space-y-2">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant="tab"
                  isActive={activeTab === tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`cht-w-full cht-justify-start cht-px-6 cht-py-4 ${activeTab === tab.id ? "cht-bg-[#3e57da] cht-shadow-lg shadow-[#3e57da]/20" : ""}`}
                  leftIcon={<tab.icon size={18} />}
                >
                  {tab.label}
                </Button>
              ))}

              <div className="cht-pt-6 cht-mt-6 cht-border-t cht-border-gray-100">
                <Button
                  variant="ghost"
                  onClick={logoutUser}
                  className="cht-w-full cht-justify-start cht-px-6 cht-py-4 cht-text-red-400 hover:cht-bg-red-50 hover:cht-text-red-500"
                  leftIcon={<LogOut size={18} />}
                >
                  Log Out
                </Button>
              </div>
            </nav>
          </aside>

          {/* Right Panel - Dynamic Content */}
          <main className="cht-flex-1 cht-p-8 md:cht-p-16 cht-bg-white">
            <div
              key={activeTab}
              className="cht-animate-in cht-fade-in cht-slide-in-from-right-8 cht-duration-500"
            >
              {activeTab === "profile-info" && <ProfileOverview />}
              {activeTab === "edit-profile" && <ProfileInformation />}
              {activeTab === "security" && (
                <div className="cht-space-y-10">
                  <div className="cht-space-y-2">
                    <h2 className="cht-text-2xl cht-font-black cht-text-black cht-tracking-tight">
                      Security & Protection
                    </h2>
                    <p className="cht-text-sm cht-text-gray-500 cht-font-medium">
                      Keep your account secure with advanced protection tools.
                    </p>
                  </div>

                  <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-6">
                    <div className="cht-p-8 cht-bg-gray-50 cht-rounded-[32px] cht-border cht-border-gray-100 cht-space-y-6">
                      <div className="cht-w-12 cht-h-12 cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-rounded-2xl cht-flex cht-items-center cht-justify-center">
                        <Smartphone size={24} />
                      </div>
                      <div className="cht-space-y-1">
                        <h4 className="cht-text-lg cht-font-black cht-text-gray-900">
                          Two-Factor Authentication
                        </h4>
                        <p className="cht-text-xs cht-text-gray-400 cht-font-medium">
                          Add an extra layer of security by requiring a code
                          from your phone.
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        className="cht-w-full cht-rounded-xl"
                      >
                        Enable 2FA
                      </Button>
                    </div>

                    <div className="cht-p-8 cht-bg-gray-50 cht-rounded-[32px] cht-border cht-border-gray-100 cht-space-y-6">
                      <div className="cht-w-12 cht-h-12 cht-bg-amber-100 cht-text-amber-600 cht-rounded-2xl cht-flex cht-items-center cht-justify-center">
                        <Shield size={24} />
                      </div>
                      <div className="cht-space-y-1">
                        <h4 className="cht-text-lg cht-font-black cht-text-gray-900">
                          Login History
                        </h4>
                        <p className="cht-text-xs cht-text-gray-400 cht-font-medium">
                          View and manage your active sessions across different
                          devices.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="cht-w-full cht-rounded-xl"
                      >
                        View Sessions
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "password" && <ChangePassword />}
            </div>
          </main>
        </div>
      </main>
    </section>
  );
};

export default Profile;
